import api from './client';

// Types
export interface Account {
  id: string;
  code: string;
  name: string;
  description?: string;
  type: AccountType;
  subType: AccountSubType;
  balance: number;
  isActive: boolean;
  parentAccountId?: string;
  metadata?: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

export enum AccountType {
  ASSET = 'ASSET',
  LIABILITY = 'LIABILITY',
  EQUITY = 'EQUITY',
  REVENUE = 'REVENUE',
  EXPENSE = 'EXPENSE'
}

export enum AccountSubType {
  CURRENT_ASSET = 'CURRENT_ASSET',
  FIXED_ASSET = 'FIXED_ASSET',
  INVENTORY = 'INVENTORY',
  BANK = 'BANK',
  CASH = 'CASH',
  RECEIVABLE = 'RECEIVABLE',
  CURRENT_LIABILITY = 'CURRENT_LIABILITY',
  LONG_TERM_LIABILITY = 'LONG_TERM_LIABILITY',
  PAYABLE = 'PAYABLE',
  CAPITAL = 'CAPITAL',
  RETAINED_EARNINGS = 'RETAINED_EARNINGS',
  OPERATING_REVENUE = 'OPERATING_REVENUE',
  OTHER_REVENUE = 'OTHER_REVENUE',
  OPERATING_EXPENSE = 'OPERATING_EXPENSE',
  ADMINISTRATIVE_EXPENSE = 'ADMINISTRATIVE_EXPENSE',
  OTHER_EXPENSE = 'OTHER_EXPENSE'
}

export interface CreateAccountDto {
  code: string;
  name: string;
  description?: string;
  type: AccountType;
  subType: AccountSubType;
  balance?: number;
  isActive?: boolean;
  parentAccountId?: string;
  metadata?: Record<string, any>;
}

export interface UpdateAccountDto {
  code?: string;
  name?: string;
  description?: string;
  type?: AccountType;
  subType?: AccountSubType;
  balance?: number;
  isActive?: boolean;
  parentAccountId?: string;
  metadata?: Record<string, any>;
}

export interface GetAccountsFilters {
  type?: AccountType;
  subType?: AccountSubType;
  isActive?: boolean;
  parentAccountId?: string;
}

// API functions
export const accountsApi = {
  // Get all accounts
  async getAccounts(filters: GetAccountsFilters = {}): Promise<Account[]> {
    const queryParams = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value.toString());
      }
    });

    const queryString = queryParams.toString();
    const url = `accounting/accounts${queryString ? `?${queryString}` : ''}`;
    
    const response = await api.get<Account[]>(url);
    return response.data;
  },

  // Get account by ID
  async getAccount(id: string): Promise<Account> {
    const response = await api.get<Account>(`accounting/accounts/${id}`);
    return response.data;
  },

  // Create account
  async createAccount(data: CreateAccountDto): Promise<Account> {
    const response = await api.post<Account>('accounting/accounts', data);
    return response.data;
  },

  // Update account
  async updateAccount(id: string, data: UpdateAccountDto): Promise<Account> {
    const response = await api.put<Account>(`accounting/accounts/${id}`, data);
    return response.data;
  },

  // Delete account
  async deleteAccount(id: string): Promise<void> {
    await api.delete(`accounting/accounts/${id}`);
  },

  /** Newer APIs seed in one shot; returns null on older tenant images. */
  async seedDefaultAccounts(): Promise<{ created: number } | null> {
    try {
      const response = await api.post<{ created: number }>('accounting/accounts/seed-defaults');
      return response.data;
    } catch {
      return null;
    }
  },

  // Get chart of accounts (hierarchical structure)
  async getChartOfAccounts(): Promise<Account[]> {
    const response = await api.get<Account[]>('accounting/accounts/chart');
    return response.data;
  },

  // Get account balance
  async getAccountBalance(id: string): Promise<{ balance: number }> {
    const response = await api.get<{ balance: number }>(`accounting/accounts/${id}/balance`);
    return response.data;
  },

  // Get accounts by type
  async getAccountsByType(type: AccountType): Promise<Account[]> {
    const response = await api.get<Account[]>(`accounting/accounts/type/${type}`);
    return response.data;
  }
};

export default accountsApi; 